import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/lib/database.types';

type Update<T extends keyof Database['public']['Tables']> =
  Database['public']['Tables'][T]['Update'];

/**
 * ניהול לומדות (PRD 5.6.1, 5.6.2, 5.6.4).
 *
 * הכתיבה כאן היא ישירות בטבלאות ולא ב RPC, כי ה RLS כבר מגביל אותה
 * ל learning.manage. מה שכן עובר בשרת: ההתראה על שיוך חדש (טריגר)
 * והדוחות (security definer), כי שניהם נוגעים בנתונים של עובדים
 * אחרים.
 */

export type AdminCourse = {
  id: string;
  title: string;
  description: string | null;
  category_id: string | null;
  is_published: boolean;
  points_reward: number;
  created_at: string;
  course_categories: { name: string } | null;
  course_lessons: { count: number }[];
  quizzes: { id: string; title: string }[];
};

export type AdminLesson = {
  id: string;
  course_id: string;
  sort_order: number;
  title: string;
  content_type_id: string;
  body: string | null;
  media_path: string | null;
  external_url: string | null;
};

/** שיעור בעריכה. סוג התוכן חובה, כי בלעדיו אי אפשר להציג אותו. */
export type LessonDraft = {
  id?: string;
  title: string;
  content_type_id: string;
  sort_order: number;
  body: string | null;
  media_path: string | null;
  external_url: string | null;
};

export type AdminQuestion = {
  id: string;
  quiz_id: string;
  sort_order: number;
  prompt: string;
  explanation: string | null;
  quiz_options: { id: string; text: string; is_correct: boolean; sort_order: number }[];
};

export type AdminAssignment = {
  id: string;
  course_id: string | null;
  quiz_id: string | null;
  branch_id: string | null;
  job_title_id: string | null;
  employee_id: string | null;
  only_new_employees_days: number | null;
  due_date: string | null;
  is_mandatory: boolean;
  created_at: string;
  courses: { title: string } | null;
  quizzes: { title: string } | null;
  branches: { name: string } | null;
  job_titles: { name: string } | null;
  employees: { first_name: string; last_name: string } | null;
};

export type LearningReport = {
  due_date: string | null;
  total: number;
  done: number;
  late: number;
  employees: {
    employee_id: string;
    name: string;
    branch: string | null;
    lessons_done: number;
    lesson_count: number;
    quiz_passed: boolean;
    best_score: number | null;
    done: boolean;
  }[];
};

export type QuestionStat = {
  question_id: string;
  prompt: string;
  sort_order: number;
  answered: number;
  correct: number;
  percent: number | null;
};

const COURSES_KEY = ['admin', 'courses'] as const;
const ASSIGNMENTS_KEY = ['admin', 'learning-assignments'] as const;

export function useAdminCourses() {
  return useQuery({
    queryKey: COURSES_KEY,
    queryFn: async (): Promise<AdminCourse[]> => {
      const { data, error } = await supabase
        .from('courses')
        .select(
          `id, title, description, category_id, is_published, points_reward, created_at,
           course_categories ( name ),
           course_lessons ( count ),
           quizzes ( id, title )`,
        )
        .order('title');
      if (error) throw error;
      return (data ?? []) as unknown as AdminCourse[];
    },
  });
}

export function useCourseEditor(courseId: string) {
  return useQuery({
    queryKey: [...COURSES_KEY, courseId],
    enabled: Boolean(courseId),
    queryFn: async () => {
      const [course, lessons, quiz] = await Promise.all([
        supabase.from('courses').select('*').eq('id', courseId).single(),
        supabase
          .from('course_lessons')
          .select('*')
          .eq('course_id', courseId)
          .order('sort_order'),
        supabase.from('quizzes').select('*').eq('course_id', courseId).maybeSingle(),
      ]);
      if (course.error) throw course.error;
      if (lessons.error) throw lessons.error;
      if (quiz.error) throw quiz.error;
      return {
        course: course.data,
        lessons: (lessons.data ?? []) as AdminLesson[],
        quiz: quiz.data,
      };
    },
  });
}

export function useQuestions(quizId: string | null) {
  return useQuery({
    queryKey: ['admin', 'quiz-questions', quizId],
    enabled: Boolean(quizId),
    queryFn: async (): Promise<AdminQuestion[]> => {
      const { data, error } = await supabase
        .from('quiz_questions')
        .select('id, quiz_id, sort_order, prompt, explanation, quiz_options ( * )')
        .eq('quiz_id', quizId as string)
        .order('sort_order');
      if (error) throw error;
      const rows = (data ?? []) as unknown as AdminQuestion[];
      // ה order של טבלה מקוננת לא נשמר ב PostgREST, ולכן ממיינים כאן
      return rows.map((q) => ({
        ...q,
        quiz_options: [...q.quiz_options].sort((a, b) => a.sort_order - b.sort_order),
      }));
    },
  });
}

/**
 * שיוכים, עם הקהל שלהם לתצוגה.
 *
 * ה foreign key של employee_id מצוין במפורש, כי לטבלה שני מפתחות ל
 * employees (גם created_by), ובלי זה PostgREST מחזיר 300.
 */
export function useAssignments() {
  return useQuery({
    queryKey: ASSIGNMENTS_KEY,
    queryFn: async (): Promise<AdminAssignment[]> => {
      const { data, error } = await supabase
        .from('learning_assignments')
        .select(
          `id, course_id, quiz_id, branch_id, job_title_id, employee_id,
           only_new_employees_days, due_date, is_mandatory, created_at,
           courses ( title ), quizzes ( title ), branches ( name ), job_titles ( name ),
           employees!learning_assignments_employee_id_fkey ( first_name, last_name )`,
        )
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as AdminAssignment[];
    },
  });
}

export function useReport(assignmentId: string) {
  return useQuery({
    queryKey: ['admin', 'learning-report', assignmentId],
    enabled: Boolean(assignmentId),
    queryFn: async (): Promise<LearningReport | null> => {
      const { data, error } = await supabase.rpc('learning_report', {
        p_assignment: assignmentId,
      });
      if (error) throw error;
      return data as LearningReport | null;
    },
  });
}

export function useQuestionStats(quizId: string | null) {
  return useQuery({
    queryKey: ['admin', 'question-stats', quizId],
    enabled: Boolean(quizId),
    queryFn: async (): Promise<QuestionStat[]> => {
      const { data, error } = await supabase.rpc('quiz_question_stats', {
        p_quiz: quizId as string,
      });
      if (error) throw error;
      return (data as QuestionStat[] | null) ?? [];
    },
  });
}

/** רשימות העזר של המסכים: קטגוריות וסוגי תוכן. */
export function useLearningLists() {
  return useQuery({
    queryKey: ['admin', 'learning-lists'],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const [categories, types] = await Promise.all([
        supabase.from('course_categories').select('id, name').order('sort_order'),
        supabase.from('lesson_content_types').select('id, name, renderer_key').order('name'),
      ]);
      if (categories.error) throw categories.error;
      if (types.error) throw types.error;
      return { categories: categories.data ?? [], types: types.data ?? [] };
    },
  });
}

export function useCourseMutations(courseId?: string) {
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: COURSES_KEY });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'quiz-questions'] });
  };

  const createCourse = useMutation({
    mutationFn: async (title: string): Promise<string> => {
      const { data, error } = await supabase
        .from('courses')
        .insert({ title })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: refresh,
  });

  const updateCourse = useMutation({
    mutationFn: async (patch: Update<'courses'>) => {
      const { error } = await supabase
        .from('courses')
        .update(patch)
        .eq('id', courseId as string);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeCourse = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('courses').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const saveLesson = useMutation({
    mutationFn: async (lesson: LessonDraft) => {
      if (lesson.id) {
        const { id, ...patch } = lesson;
        const { error } = await supabase.from('course_lessons').update(patch).eq('id', id);
        if (error) throw error;
        return;
      }
      const { error } = await supabase
        .from('course_lessons')
        .insert({ ...lesson, course_id: courseId as string });
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeLesson = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('course_lessons').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  /** החלפת מקום בין שני שיעורים, לשינוי הסדר. */
  const swapLessons = useMutation({
    mutationFn: async (pair: [AdminLesson, AdminLesson]) => {
      const [a, b] = pair;
      const first = await supabase
        .from('course_lessons')
        .update({ sort_order: b.sort_order })
        .eq('id', a.id);
      if (first.error) throw first.error;
      const second = await supabase
        .from('course_lessons')
        .update({ sort_order: a.sort_order })
        .eq('id', b.id);
      if (second.error) throw second.error;
    },
    onSuccess: refresh,
  });

  const createQuiz = useMutation({
    mutationFn: async (title: string): Promise<string> => {
      const { data, error } = await supabase
        .from('quizzes')
        .insert({ title, course_id: courseId as string })
        .select('id')
        .single();
      if (error) throw error;
      return data.id;
    },
    onSuccess: refresh,
  });

  const updateQuiz = useMutation({
    mutationFn: async (input: { id: string; patch: Update<'quizzes'> }) => {
      const { error } = await supabase.from('quizzes').update(input.patch).eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return {
    createCourse,
    updateCourse,
    removeCourse,
    saveLesson,
    removeLesson,
    swapLessons,
    createQuiz,
    updateQuiz,
  };
}

export type QuestionDraft = {
  id?: string;
  prompt: string;
  explanation: string | null;
  options: { id?: string; text: string; is_correct: boolean }[];
};

export function useQuestionMutations(quizId: string | null) {
  const queryClient = useQueryClient();
  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ['admin', 'quiz-questions', quizId] });

  /**
   * שאלה והמסיחים שלה נשמרים יחד: שאלה בלי אף תשובה נכונה היא שאלה
   * שאי אפשר לענות עליה נכון, ולכן אין טעם לשמור אותן בנפרד.
   */
  const saveQuestion = useMutation({
    mutationFn: async (draft: QuestionDraft) => {
      let questionId = draft.id;

      if (questionId) {
        const { error } = await supabase
          .from('quiz_questions')
          .update({ prompt: draft.prompt, explanation: draft.explanation })
          .eq('id', questionId);
        if (error) throw error;
        // המסיחים נכתבים מחדש, כי שינוי סדר או מחיקה הם המקרה הרגיל
        const cleared = await supabase.from('quiz_options').delete().eq('question_id', questionId);
        if (cleared.error) throw cleared.error;
      } else {
        const { count } = await supabase
          .from('quiz_questions')
          .select('id', { count: 'exact', head: true })
          .eq('quiz_id', quizId as string);
        const { data, error } = await supabase
          .from('quiz_questions')
          .insert({
            quiz_id: quizId as string,
            prompt: draft.prompt,
            explanation: draft.explanation,
            sort_order: (count ?? 0) + 1,
          })
          .select('id')
          .single();
        if (error) throw error;
        questionId = data.id;
      }

      const { error } = await supabase.from('quiz_options').insert(
        draft.options.map((option, i) => ({
          question_id: questionId as string,
          text: option.text,
          is_correct: option.is_correct,
          sort_order: i + 1,
        })),
      );
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const removeQuestion = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('quiz_questions').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { saveQuestion, removeQuestion };
}

export function useAssignmentMutations() {
  const queryClient = useQueryClient();
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ASSIGNMENTS_KEY });

  const create = useMutation({
    mutationFn: async (input: {
      course_id: string | null;
      quiz_id: string | null;
      branch_id: string | null;
      job_title_id: string | null;
      employee_id: string | null;
      only_new_employees_days: number | null;
      due_date: string | null;
      is_mandatory: boolean;
    }) => {
      const { error } = await supabase.from('learning_assignments').insert(input);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('learning_assignments').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return { create, remove };
}

/** מי מקבל את השיוך, בשורה אחת שאפשר לקרוא. */
export function audienceLabel(assignment: AdminAssignment): string {
  const parts: string[] = [];
  if (assignment.employees) {
    parts.push(`${assignment.employees.first_name} ${assignment.employees.last_name}`);
  }
  if (assignment.branches) parts.push(`סניף ${assignment.branches.name}`);
  if (assignment.job_titles) parts.push(assignment.job_titles.name);
  if (assignment.only_new_employees_days !== null) {
    parts.push(`מי שהתחיל ב ${assignment.only_new_employees_days} הימים האחרונים`);
  }
  return parts.length > 0 ? parts.join(' · ') : 'כל הרשת';
}
